import React, { useEffect, useState } from 'react'

import FeatureNotEnabled from '../../components/FeatureNotEnabled'
import { supabase } from '../../lib/supabase'
import { Card } from '../../components/UI'
import {
    Radio, Wifi, WifiOff, Activity, Clock, ArrowUpDown,
    Zap, Shield, RefreshCw, Settings2, Info, ChevronDown
} from 'lucide-react'

// ============================================
// WebSockets — Control en Tiempo Real
// ============================================

type ConnectionState = 'connected' | 'connecting' | 'disconnected' | 'reconnecting'

interface WSEvent {
    time: string
    type: 'connect' | 'disconnect' | 'message' | 'error' | 'reconnect'
    detail: string
}

export default function WebSocketsPage() {
    const [connState, setConnState] = useState<ConnectionState>('disconnected')
    const [events, setEvents] = useState<WSEvent[]>([
        { time: '03:15:00', type: 'connect', detail: 'Conexión establecida con Supabase Realtime' },
        { time: '03:14:55', type: 'message', detail: 'license_update: Autogest360 → features updated' },
        { time: '03:14:30', type: 'message', detail: 'command_new: redirect → Autogest360' },
        { time: '03:10:00', type: 'reconnect', detail: 'Reconexión automática tras timeout (30s)' },
        { time: '03:09:30', type: 'disconnect', detail: 'WebSocket cerrado por inactividad del servidor' },
    ])
    const [tab, setTab] = useState<'live' | 'channels' | 'config' | 'migration'>('live')
    const [stats, setStats] = useState({ messagesReceived: 47, latencyMs: 12, uptime: '2h 15m', reconnects: 3 })

    const stateStyles: Record<ConnectionState, { bg: string; border: string; text: string; label: string }> = {
        connected: { bg: 'bg-neon-green/5', border: 'border-neon-green/20', text: 'text-neon-green', label: 'CONECTADO' },
        connecting: { bg: 'bg-blue-500/5', border: 'border-blue-500/20', text: 'text-blue-400', label: 'CONECTANDO...' },
        disconnected: { bg: 'bg-red-500/5', border: 'border-red-500/20', text: 'text-red-400', label: 'DESCONECTADO' },
        reconnecting: { bg: 'bg-yellow-500/5', border: 'border-yellow-500/20', text: 'text-yellow-400', label: 'RECONECTANDO...' },
    }

    const eventTypeStyles: Record<string, string> = {
        connect: 'text-neon-green bg-neon-green/10',
        disconnect: 'text-red-400 bg-red-500/10',
        message: 'text-blue-400 bg-blue-500/10',
        error: 'text-red-400 bg-red-500/10',
        reconnect: 'text-yellow-400 bg-yellow-500/10',
    }

    const simulateConnect = () => {
        setConnState('connecting')
        setTimeout(() => {
            setConnState('connected')
            setEvents(prev => [{ time: new Date().toLocaleTimeString('es-ES'), type: 'connect', detail: 'Conexión WebSocket establecida' }, ...prev].slice(0, 50))
        }, 1500)
    }

    const simulateDisconnect = () => {
        setConnState('disconnected')
        setEvents(prev => [{ time: new Date().toLocaleTimeString('es-ES'), type: 'disconnect', detail: 'Desconectado manualmente' }, ...prev].slice(0, 50))
    }

    const current = stateStyles[connState]
    const tabs = [
        { id: 'live' as const, label: 'Live Monitor', icon: <Activity size={16} /> },
        { id: 'channels' as const, label: 'Canales', icon: <Radio size={16} /> },
        { id: 'config' as const, label: 'Configuración', icon: <Settings2 size={16} /> },
        { id: 'migration' as const, label: 'Guía Migración', icon: <Info size={16} /> },
    ]

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <Radio className="text-neon-blue" /> WebSockets — Tiempo Real
                </h1>
                <p className="text-gray-500 text-sm">Supabase Realtime — Reemplazo del polling HTTP</p>
            </div>

            <FeatureNotEnabled moduleName="WebSockets" />

            {/* Estado de conexión */}
            <Card className={`mb-6 ${current.bg} border ${current.border}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {connState === 'connected' ? <Wifi size={28} className={current.text} /> : <WifiOff size={28} className={current.text} />}
                        <div>
                            <div className="flex items-center gap-3">
                                <span className={`text-xl font-bold font-mono ${current.text}`}>{current.label}</span>
                                {connState === 'connected' && <span className="w-2.5 h-2.5 rounded-full bg-neon-green animate-pulse" />}
                            </div>
                            <p className="text-gray-500 text-xs mt-0.5">wss://jgsiqltbcffwmtpitrhy.supabase.co/realtime/v1</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={simulateConnect} disabled={connState === 'connected'} className="px-3 py-2 bg-neon-green/10 text-neon-green border border-neon-green/20 rounded-lg text-xs font-medium hover:bg-neon-green/20 transition-all disabled:opacity-30">
                            <Wifi size={14} className="inline mr-1" /> Conectar
                        </button>
                        <button onClick={simulateDisconnect} disabled={connState === 'disconnected'} className="px-3 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-xs font-medium hover:bg-red-500/20 transition-all disabled:opacity-30">
                            <WifiOff size={14} className="inline mr-1" /> Desconectar
                        </button>
                    </div>
                </div>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                    { label: 'Mensajes', value: stats.messagesReceived, icon: <ArrowUpDown size={16} />, color: 'text-blue-400' },
                    { label: 'Latencia', value: `${stats.latencyMs}ms`, icon: <Zap size={16} />, color: 'text-neon-green' },
                    { label: 'Uptime', value: stats.uptime, icon: <Clock size={16} />, color: 'text-purple-400' },
                    { label: 'Reconexiones', value: stats.reconnects, icon: <RefreshCw size={16} />, color: 'text-yellow-400' },
                ].map((s, i) => (
                    <Card key={i} className="!p-4 text-center">
                        <div className={`${s.color} mb-2 flex justify-center`}>{s.icon}</div>
                        <div className="text-xl font-bold font-mono text-white">{s.value}</div>
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-1">{s.label}</div>
                    </Card>
                ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 bg-charcoal rounded-lg p-1 border border-white/5">
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : 'text-gray-500 hover:text-white'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'live' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2"><Activity size={16} className="text-neon-blue" /> Event Stream</h2>
                    <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
                        {events.map((ev, i) => (
                            <div key={i} className="flex items-center gap-3 p-3 bg-black/20 rounded-lg text-sm">
                                <span className="text-gray-600 font-mono text-xs w-16 shrink-0">{ev.time}</span>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${eventTypeStyles[ev.type]}`}>{ev.type}</span>
                                <span className="text-gray-400 text-xs truncate">{ev.detail}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {tab === 'channels' && (
                <div className="space-y-3">
                    {[
                        { name: 'licenses', desc: 'Cambios en tabla licenses (INSERT, UPDATE, DELETE)', status: 'active', events: 23 },
                        { name: 'remote_commands', desc: 'Nuevos comandos remotos (INSERT)', status: 'active', events: 8 },
                        { name: 'telemetry_events', desc: 'Eventos de telemetría (INSERT)', status: 'paused', events: 156 },
                        { name: 'command_audit_log', desc: 'Auditoría de comandos (INSERT)', status: 'active', events: 12 },
                    ].map(ch => (
                        <Card key={ch.name} className="!p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Radio size={16} className={ch.status === 'active' ? 'text-neon-green' : 'text-gray-600'} />
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <code className="text-neon-blue text-sm font-mono">{ch.name}</code>
                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${ch.status === 'active' ? 'bg-neon-green/10 text-neon-green' : 'bg-gray-500/10 text-gray-500'}`}>{ch.status}</span>
                                        </div>
                                        <p className="text-gray-500 text-xs mt-0.5">{ch.desc}</p>
                                    </div>
                                </div>
                                <span className="text-gray-600 text-xs font-mono">{ch.events} eventos</span>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {tab === 'config' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-6 flex items-center gap-2"><Settings2 size={16} className="text-neon-blue" /> Parámetros WebSocket</h2>
                    <div className="space-y-4">
                        {[
                            { key: 'heartbeatInterval', label: 'Heartbeat Interval', value: '30s', desc: 'Intervalo de ping/pong para mantener la conexión' },
                            { key: 'reconnectDelay', label: 'Reconnect Delay', value: '5s', desc: 'Tiempo antes de intentar reconexión automática' },
                            { key: 'maxReconnects', label: 'Max Reconnects', value: '10', desc: 'Máximo de intentos de reconexión antes de fallback a polling' },
                            { key: 'timeout', label: 'Connection Timeout', value: '10s', desc: 'Timeout máximo para establecer conexión' },
                        ].map(p => (
                            <div key={p.key} className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
                                <div>
                                    <span className="text-white text-sm font-medium">{p.label}</span>
                                    <p className="text-gray-600 text-xs mt-0.5">{p.desc}</p>
                                </div>
                                <code className="text-neon-blue text-sm font-mono bg-neon-blue/5 px-2 py-1 rounded">{p.value}</code>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {tab === 'migration' && (
                <div className="space-y-4">
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2"><Info size={16} className="text-neon-blue" /> Polling → WebSockets</h2>
                        <div className="text-gray-400 text-sm space-y-3 leading-relaxed">
                            <p>El agente <code className="text-neon-blue bg-neon-blue/5 px-1 rounded">vendor_core.ts</code> actualmente usa <strong>HTTP polling</strong> cada 60s para consultar comandos.</p>
                            <p>La migración a <strong>Supabase Realtime</strong> (WebSockets) reduce latencia de ~60s a ~100ms y elimina requests innecesarios.</p>
                        </div>
                    </Card>
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-4">Comparativa</h2>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead><tr className="border-b border-white/5">
                                    <th className="text-left text-gray-500 py-2 px-3 text-xs">Métrica</th>
                                    <th className="text-center text-red-400 py-2 px-3 text-xs">HTTP Polling</th>
                                    <th className="text-center text-neon-green py-2 px-3 text-xs">WebSockets</th>
                                </tr></thead>
                                <tbody className="text-gray-300">
                                    {[
                                        ['Latencia', '30-60s', '~100ms'],
                                        ['Requests/hora', '60', '0 (push)'],
                                        ['Bandwidth', 'Alto (headers)', 'Mínimo (frames)'],
                                        ['Reconexión', 'N/A', 'Automática'],
                                        ['Bidireccional', '❌', '✅'],
                                    ].map(([metric, poll, ws], i) => (
                                        <tr key={i} className="border-b border-white/5">
                                            <td className="py-2.5 px-3 text-gray-400">{metric}</td>
                                            <td className="py-2.5 px-3 text-center font-mono text-xs">{poll}</td>
                                            <td className="py-2.5 px-3 text-center font-mono text-xs">{ws}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-4">Implementación Supabase Realtime</h2>
                        <pre className="bg-black/30 rounded-lg p-4 text-xs text-gray-300 font-mono overflow-auto border border-white/5">{`// vendor_core.ts — Migración a WebSockets
const channel = supabase
  .channel('commands')
  .on('postgres_changes', {
    event: 'INSERT',
    schema: 'public',
    table: 'remote_commands',
    filter: \`license_id=eq.\${LICENSE_ID}\`
  }, (payload) => {
    handleCommand(payload.new)
  })
  .subscribe()

// Fallback automático a polling si WS falla
channel.on('error', () => {
  console.warn('[Torre] WS error, falling back to polling')
  startPolling()
})`}</pre>
                    </Card>
                </div>
            )}
        </div>
    )
}
